import { InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import axios, { AxiosRequestConfig } from 'axios'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogContent, FormControlLabel, IconButton, Link, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setReport } from '../../source/slice/ReportSlice'
import ReportLines from './ReportLines'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import BarChartIcon from '@mui/icons-material/BarChart'
import { AccessAlarm } from '@mui/icons-material'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import { useReactToPrint } from 'react-to-print'
import { de } from 'date-fns/locale/de'
import { enUS } from 'date-fns/locale/en-US'
import Statistics from './Statistics'
import OnCallShifts from './OnCallShifts'
import ReportPrintDetails from './ReportPrintDetails'
import LaunchIcon from '@mui/icons-material/Launch'
import ReportPrintDetailsOrder from './ReportPrintDetailsOrder'
import { IUser } from '../../interface/IUser'
import { getAuthConfig } from '../../utils/authApi'
import { IReport } from '../../interface/IReport'
import filterReportData from '../../utils/functions/filterReportData'

const Report = () => {
    const { instance, accounts } = useMsal()
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const contentRef = useRef<HTMLDivElement>(null)
    const user: IUser = useSelector((state: any) => state.user?.value)
    const reportData: IReport[] = useSelector((state: any) => state.report?.value)
    const [open, setOpen] = useState(false)

    const [reportType, setReportType] = useState<string | null>('table')
    const [loading, setLoading] = useState<boolean>(false)
    const [expandAll, setExpandAll] = useState<boolean>(false)
    const hasTimeCard = user?.worker?.mserp_usetimecard
    const reportURL = `${process.env.REACT_APP_BACKEND_URL}/${hasTimeCard ? 'Report' : 'ReportFAK'}`

    const [selectedMonth, setSelectedMonth] = useState<Date | null>(null)

    const scope : string = process.env.REACT_APP_SCOPE || ''
    const accessTokenRequest: SilentRequest = {
        scopes: [scope],
        account: accounts[0],
        forceRefresh: false,
    }

    useEffect(() => {
        if (selectedMonth) {
            getSelectedDate(selectedMonth)
        }
    }, [selectedMonth])

    useEffect(() => {
        if (reportType === 'oncall') {
            const filtered = filterReportData(reportData)
            if (filtered.length !== reportData.length) {
                dispatch(setReport(filtered))
            }
        }
    }, [reportType, selectedMonth, reportData])

    const reactToPrintFn = useReactToPrint({
        contentRef,
        fonts: [{ family: 'MB Corpo S Text', source: '' }],
        documentTitle: `${selectedMonth ? format(selectedMonth, 'yyyy-MM') : ''}-Timetracking`,
    })

    const getSelectedDate = async (e: any) => {
        setLoading(true)
        if (selectedMonth && selectedMonth !== null) {
            getAuthConfig(instance, accounts[0])
                .then((headers) => {
                    const config: AxiosRequestConfig = {
                        ...headers,
                        data: {},
                        params: {
                            date: format(e, 'yyyy-MM-01'),
                        },
                    }
                    axios
                        .get(reportURL, config)
                        .then((response) => {
                            dispatch(setReport(response.data))
                            setLoading(false)
                        })
                        .catch((error) => {
                            console.log(error)
                            setLoading(false)
                        })
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        return instance.acquireTokenRedirect(accessTokenRequest)
                    }
                    console.log(error)
                    setLoading(false)
                })
        } else {
            setLoading(false)
        }
    }

    const handleReportType = (event: React.MouseEvent<HTMLElement>, newReportType: string | null) => {
        if (newReportType !== null) setReportType(newReportType)
    }

    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    const printDocument = () => {
        reactToPrintFn()
    }

    return (
        <>
            <IconButton size="large" color="inherit" onClick={handleClickOpen}>
                <AssessmentOutlinedIcon />
            </IconButton>
            <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description" maxWidth={'xl'} scroll={'body'} sx={{ minWidth: 'lg' }}>
                {/* <DialogTitle id="alert-dialog-title">{'Monatsreport'}</DialogTitle> */}
                <Grid display="flex" container alignItems="center" spacing={2}>
                    <Grid size={8}>
                        <Box display="flex" alignItems="center" m={2} ml={4}>
                            <Typography variant="h4" component="h1">
                                {t('report.title')}
                            </Typography>
                            <Link href="https://corpdir.sharepoint.com/sites/mbti-apps-and-tools/SitePages/Reports.aspx" target="_blank">
                                <Chip label="HelpCenter" color="primary" variant="outlined" size="small" deleteIcon={<LaunchIcon />} onDelete={() => {}} />
                            </Link>
                        </Box>
                    </Grid>
                    <Grid size={4}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" m={2} ml={4}>
                            <Tooltip title={t('report.refresh')}>
                                <IconButton aria-label="refresh data" size="small" onClick={() => getSelectedDate(selectedMonth)} disabled={loading || !selectedMonth}>
                                    <AutorenewIcon />
                                </IconButton>
                            </Tooltip>
                            <ToggleButtonGroup size="small" value={reportType} onChange={handleReportType} exclusive unselectable="off">
                                <ToggleButton value={'table'} color={reportType === 'table' ? 'primary' : 'standard'}>
                                    <Tooltip title={t('report.table')}>
                                        <TableChartOutlinedIcon />
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value={'chart'} color={reportType === 'chart' ? 'primary' : 'standard'} disabled={!hasTimeCard}>
                                    <Tooltip title={t('report.chart')}>
                                        <BarChartIcon />
                                    </Tooltip>
                                </ToggleButton>
                                {user.worker?.mserp_mbrecordoncallduty && (
                                    <ToggleButton value={'oncall'} color={reportType === 'oncall' ? 'primary' : 'standard'}>
                                        <Tooltip title={t('report.oncall')}>
                                            <AccessAlarm />
                                        </Tooltip>
                                    </ToggleButton>
                                )}
                            </ToggleButtonGroup>
                        </Box>
                    </Grid>
                </Grid>
                <Box display="flex" justifyContent="space-between" alignItems="center" mr={2} ml={4}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={i18n.language === 'de' ? de : enUS}>
                        <DatePicker
                            label={t('report.month')}
                            openTo="month"
                            views={['year', 'month']}
                            slotProps={{ textField: { size: 'small' } }}
                            onChange={(e: Date | null) => setSelectedMonth(e)}
                            defaultValue={selectedMonth ?? null}
                            minDate={new Date(2024, 12, 31)}
                            maxDate={new Date()}
                        />
                    </LocalizationProvider>
                    <Box display="flex" gap={2}>
                        {reportType === 'table' ? (
                            <>
                                <FormControlLabel value="end" control={<Checkbox />} label={t('report.expandall')} labelPlacement="end" checked={expandAll} onChange={() => setExpandAll(!expandAll)} disabled={loading || !selectedMonth} />
                                <Button variant="outlined" size="small" startIcon={<PrintOutlinedIcon />} onClick={() => printDocument()} disabled={loading || !selectedMonth}>
                                    {t('report.print')}
                                </Button>
                            </>
                        ) : (
                            <></>
                        )}
                    </Box>
                </Box>
                <DialogContent ref={contentRef} sx={{ minWidth: '45vw' }}>
                    {loading && (
                        <Box display="flex" justifyContent="center">
                            <CircularProgress color="primary" />
                        </Box>
                    )}
                    {!loading && selectedMonth && (
                        <>
                            {reportType === 'table' && (
                                <>
                                    <ReportPrintDetails month={selectedMonth} />
                                    <ReportLines expandAll={expandAll} hasTimeCard={hasTimeCard} />
                                    {(user.worker.mserp_employmentcategory === 'ANÜ' || user.worker.mserp_employmentcategory === 'FAK') && <ReportPrintDetailsOrder type={user.worker.mserp_employmentcategory} />}
                                </>
                            )}
                            {reportType === 'chart' && <Statistics />}
                            {reportType === 'oncall' && <OnCallShifts date={selectedMonth} />}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}

export default Report
